import { LinearProgress } from '@mui/material';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import classnames from 'classnames';
import * as moment from 'moment';
import type { ReactNode, VoidFunctionComponent } from 'react';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Locator from '../../../shared/Locator';
import BuildModule from '../../redux/builds';
import TaskModule from '../../redux/tasks';
import type { BuildStep } from '../../redux/tasks/constants';
import type { Revision, Task } from '../../types';
import { trimString } from '../../utils';
import { Badge, BadgeColors, BadgeShapes } from '../generic';
import Flex from '../generic/Flex';
import styles from './ProjectBuildItem.module.less';

const BuildSteps = TaskModule.constants.BuildSteps;

type Props = {
  /**
   * Build id
   */
  id: number;
  /**
   * Revision being Built
   */
  revision?: Revision;
  /**
   * Task of the Build
   */
  task?: Task;
  /**
   * Build error
   */
  error?: string;
  /**
   * function to be called when then build goes from a non successful to successful state
   */
  onComplete?: () => void;
};

const BuildStepMap = {
  [BuildSteps.Failed]: -1,
  [BuildSteps.Queued]: 0,
  [BuildSteps.ApplyingBuild]: 1,
  [BuildSteps.AnalyzingDeps]: 2,
  [BuildSteps.Succeeded]: 3,
};

type BuildProgressProps = {
  buildStep: BuildStep;
};

const BuildProgress: VoidFunctionComponent<BuildProgressProps> = ({
  buildStep,
}) => {
  return (
    <div>
      <Stepper activeStep={BuildStepMap[buildStep]}>
        <Step>
          <StepLabel>Queued</StepLabel>
        </Step>
        <Step>
          <StepLabel>Applying build data</StepLabel>
        </Step>
        <Step>
          <StepLabel>Analyzing dependencies</StepLabel>
        </Step>
        <Step>
          <StepLabel>Finished</StepLabel>
        </Step>
      </Stepper>
      {
        buildStep !== BuildSteps.Failed &&
        buildStep !== BuildSteps.Succeeded &&
        <div className="margin-top-lg">
          <LinearProgress />
        </div>
      }
    </div>
  );
};

type BuildTimeProps = {
  title: string | ReactNode;
  time?: string;
  align?: 'left' | 'right';
};

const BuildTime: VoidFunctionComponent<BuildTimeProps> = ({ title, time, align = 'left' }) =>
  <Flex direction="column" align="flex-start">
    <div className="fossa-font-weight-semibold" style={{ alignSelf: align === 'left' ? 'start' : 'end' }}>
      {title}
    </div>
    <div>
      { time ? moment(time).format('MMMM Do YYYY, h:mm:ss a') : 'N/A' }
    </div>
  </Flex>;

const ProjectBuildItem: VoidFunctionComponent<Props> = ({
  revision,
  task,
  id,
  error,
  onComplete,
}) => {
  const dispatch = useDispatch();
  const buildStep = TaskModule.fns.getBuildStep(task, revision?.isSteady);

  // Calls a function upon build completion
  useEffect(() => {
    if (onComplete && buildStep === BuildSteps.Succeeded) {
      onComplete();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildStep]);

  useEffect(() => {
    if (task) {
      if (!TaskModule.fns.isTaskFinished(task)) {
        dispatch(BuildModule.actions.watchBuild(id));
      } else {
        dispatch(BuildModule.actions.stopWatchingBuild(id));
      }
    }
    // Cleanup. Stop polling on unmount
    return () => {
      dispatch(BuildModule.actions.stopWatchingBuild(id));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task, id]);

  const revId = revision?.locator && new Locator(revision?.locator).revision.value;
  const startTitle = buildStep === 'QUEUED' ? 'Submitted' : 'Started';
  const startTime = buildStep === 'QUEUED' ? task?.createdAt : task?.started;

  return (
    <div className={classnames(
      styles.buildCard,
      'card',
      'padded-lg',
      {
        [styles.successfulBuild]: buildStep === 'SUCCEEDED',
        [styles.failedBuild]: buildStep === 'FAILED',
      },
    )}>
      <Flex direction="column" align="normal">
        <div className="margin-bottom-md">
          <Flex justify="space-between">
            <div>
              <h3 className="nomargin-v">#{id}</h3>
            </div>
            {revId &&
              <Badge
                color={BadgeColors.DarkGray}
                shape={BadgeShapes.Circle}
              >
                Revision ID: {revId}
              </Badge>}
          </Flex>
        </div>
        {revision?.message &&
          <div className="margin-bottom-lg">
            <span className="fossa-font-weight-semibold">Commit message</span>
            <p>{trimString(revision.message, 100)}</p>
          </div>}
        <BuildProgress buildStep={buildStep} />
        {error && buildStep === 'FAILED' &&
          <div className="alert alert-error contained-error margin-top-md margin-bottom-md">
            {error}
          </div>}
        <div className={styles.buildTimeSection}>
          <Flex justify="space-between">
            <BuildTime title={startTitle} time={startTime} />
            { task &&
              [BuildSteps.Succeeded, BuildSteps.Failed].some(bs => bs === buildStep) &&
              TaskModule.fns.isTaskFinished(task) &&
                <BuildTime
                  title={<Badge color={buildStep === 'SUCCEEDED' ? BadgeColors.Green : BadgeColors.Red}>{buildStep}</Badge>}
                  time={task.finished}
                  align="right"
                />}
          </Flex>
        </div>
      </Flex>
    </div>
  );
};

export default ProjectBuildItem;
